import React, { useContext, useEffect, useState } from 'react';
import { graphql } from 'react-relay';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';
// We have deprecated unstated. Please use react-sweet-state instead
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button, { LinkButton } from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';
import { FadeIn } from '@atlaskit/motion';
import { Inline, Text, Box, xcss } from '@atlaskit/primitives';
import ClockIcon from '@atlaskit/icon/core/clock';
import traceUFOTransition from '@atlaskit/react-ufo/trace-transition';

import { PageLoadEnd } from '@confluence/browser-metrics/entry-points/PageLoadEnd.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError as is403 } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { isUnauthorizedError as is401 } from '@confluence/error-types/entry-points/isUnauthorizedError.ts';
import { RESTRICTED_PAGE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStart,
	ExperienceSuccess,
	ExperienceFailure,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { shouldShowMobileWeb } from '@confluence/mobile-detection';
import { VIEW_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { ErrorPageComponent } from '@confluence/not-found/entry-points/ErrorPageComponent.ts';
import { useContentType } from '@confluence/page-context';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useFeatureCodesInURL } from '@confluence/feature-codes/entry-points/useFeatureCodesInURL.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { usePendingRequestExists } from '@confluence/access-requests/entry-points/usePendingRequestExists.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { VIEW_PAGE_LOAD_KEY } from '@confluence/browser-metrics/entry-points/metric-keys.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import type {
	RestrictedPageWithCanViewQuery$data,
	RestrictedPageWithCanViewQuery$variables,
} from './__generated__/RestrictedPageWithCanViewQuery.graphql.ts';
import { INACCESSIBLE_PAGE_LOAD } from './perf.config.js';
import CantViewLightMode from './CantViewLight.svg';
import CantViewDarkMode from './CantViewDark.svg';
import CantEditLightMode from './CantEditLight.svg';
import CantEditDarkMode from './CantEditDark.svg';
import { SuperAdminGateway } from './SuperAdminGateway.js';
import type {
	RestrictedPageRequestPageAccessMutation$data,
	RestrictedPageRequestPageAccessMutation$variables,
} from './__generated__/RestrictedPageRequestPageAccessMutation.graphql.ts';
import { ShowSpinnerAfterDelay } from './ShowSpinnerAfterDelay.tsx';

const RestrictedPageWithCanViewQueryGraphQL = graphql`
	query RestrictedPageWithCanViewQuery($id: ID!) {
		singleContent(id: $id) {
			id
			status
			links {
				webui
			}
		}
		user: confluence_atlassianUser(current: true) {
			id
			confluence {
				roles {
					canBeSuperAdmin
				}
			}
		}
	}
`;

const RestrictedPageRequestPageAccessMutationGraphQL = graphql`
	mutation RestrictedPageRequestPageAccessMutation(
		$requestPageAccessInput: RequestPageAccessInput!
	) {
		requestPageAccess(requestPageAccessInput: $requestPageAccessInput) {
			displayName
		}
	}
`;

const SUCCESS_FLAG_ID = 'reqestAccessSuccess';

type RestrictedPageProps = {
	accessType: 'inherited' | 'view' | 'edit';
	contentId: string | undefined | null;
	showSuperAdminGateway?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonContainer = styled.div<{ isMobile: boolean }>({
	marginTop: token('space.300'),
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	justifyContent: ({ isMobile }) => (isMobile ? 'center' : 'left'),
	gap: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const buttonPlaceholderStyles = xcss({
	marginLeft: 'space.100',
	height: '32px',
	width: '32px',
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RequestConfirmationTextContainer = styled.div({
	alignItems: 'center',
	justifyContent: 'center',
	display: 'flex',
	height: '32px',
	paddingLeft: token('space.100'),
	paddingRight: token('space.100'),
});

const CUSTOM_ATTRIBUTES = {
	inaccessiblePageType: 'RESTRICTED_PAGE',
};

const i18n = defineMessages({
	requestSentTitle: {
		id: 'no-permission.restricted.page.request.access.successful.title.2',
		defaultMessage: 'Request sent',
		description: 'Title of notification after request successfully sent',
	},
	requestFailedTitle: {
		id: 'no-permission.restricted.page.request.access.failed.title',
		defaultMessage: 'Access request unsuccessful',
		description: 'Title of notification after request failed',
	},
	requestFailedBody: {
		id: 'no-permission.restricted.page.request.access.failed.message.2',
		defaultMessage:
			'We ran into a problem submitting your access request. Try again or contact your admin.',
		description: 'Message body of notification after request failed',
	},
});

const RestrictedPageComponent = ({
	accessType,
	contentId,
	editDeniedButCanView,
	flags,
	viewLink,
	showSuperAdminGateway = true,
}: RestrictedPageProps &
	WithFlagsProps & {
		editDeniedButCanView: boolean;
		viewLink: string | null;
	}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [contentType] = useContentType();
	const [requestSent, setRequestSent] = useState(false);
	const renderServerPlaceholder = useRenderServerPlaceholder();

	const {
		pendingRequestExists,
		isLoadingForFirstTime,
		error: pendingAccessRequestExistsError,
	} = usePendingRequestExists({ contentId });

	const { from } = useFeatureCodesInURL();

	useEffect(() => {
		// Yes, it's always a 'page' viewed, even if contentType is something
		// else. It's just how this screen is registered.
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'restrictedPageViewed',
				action: 'viewed',
				actionSubject: 'page',
				actionSubjectId: 'restrictedPage',
				attributes: {
					accessType: accessType || 'unspecified',
					contentId,
					contentType,
					editDeniedButCanView,
					from,
				},
			},
		}).fire();
	}, [
		accessType,
		contentId,
		contentType,
		editDeniedButCanView,
		viewLink,
		createAnalyticsEvent,
		from,
	]);

	const makeLinkClickedHandler = (buttonLocation: string) => () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'viewPageButton',
				source: 'restrictedPageScreen',
				attributes: {
					buttonLocation,
					accessType: accessType || 'unspecified',
					contentId,
					contentType,
				},
			},
		}).fire();

		// If the link was in a flag, hide the flag, since this might be an
		// SPA transition.
		void flags.hideFlag(SUCCESS_FLAG_ID);
	};

	/** Displays the success or failure flags after access is requested */
	const handleRequestComplete = (result: RestrictedPageRequestPageAccessMutation$data) => {
		const requestSuccessful = result?.requestPageAccess?.displayName;

		if (requestSuccessful) {
			void flags.showSuccessFlag({
				isAutoDismiss: true,
				id: SUCCESS_FLAG_ID,
				title: formatMessage(i18n.requestSentTitle),
				actions: editDeniedButCanView
					? [
							{
								content: (
									<FormattedMessage
										id="no-permission.restricted.page.flag.view-content"
										description="Link button label for viewing a page/blog/whiteboard/etc. the user can't edit"
										defaultMessage="View the content now"
									/>
								),
								onClick: makeLinkClickedHandler('access-requested-flag'),
								href: viewLink!,
							},
						]
					: undefined,
			});
		} else {
			void flags.showErrorFlag({
				isAutoDismiss: true,
				title: formatMessage(i18n.requestFailedTitle),
				description: formatMessage(i18n.requestFailedBody),
			});
		}

		setRequestSent(true);
	};

	const handleRequestError = () => {
		void flags.showErrorFlag({
			isAutoDismiss: true,
			title: formatMessage(i18n.requestFailedTitle),
			description: formatMessage(i18n.requestFailedBody),
		});
		setRequestSent(true);
	};

	const [requestAccess] = useMutationInterop<
		RestrictedPageRequestPageAccessMutation$data,
		RestrictedPageRequestPageAccessMutation$variables
	>(RestrictedPageRequestPageAccessMutationGraphQL, {
		variables: {
			requestPageAccessInput: {
				pageId: contentId ?? '',
				accessType: accessType === 'edit' ? 'EDIT' : 'VIEW',
			},
		},
		onCompleted: handleRequestComplete,
		onError: handleRequestError,
	});

	const renderTitle = () =>
		accessType === 'edit' ? (
			editDeniedButCanView ? (
				<FormattedMessage
					id="no-permission.restricted.page.edit-but-can-view.title"
					description="Title displayed for a page which the user cannot edit but could view"
					defaultMessage="You don’t have access to edit this content"
				/>
			) : (
				<FormattedMessage
					id="no-permission.restricted.page.edit.title"
					description="Title displayed for a page that has editing restricted"
					defaultMessage="Editing is restricted for this content"
				/>
			)
		) : (
			<FormattedMessage
				id="no-permission.restricted.page.view.title"
				description="Title displayed for a page that has viewing restricted"
				defaultMessage="You’ve stumbled on restricted content"
			/>
		);

	const renderMessage = () =>
		accessType === 'inherited' ? (
			<FormattedMessage
				id="no-permission.restricted.page.inherited.message"
				description="Message displayed when a page/content restriction is inherited from parent"
				defaultMessage="This is because it’s inheriting restrictions from a parent item. A space admin or the person who shared this content may be able to give you access."
			/>
		) : (
			renderAccessRequest()
		);

	const handleRequestAccessClick = async () => {
		try {
			setRequestAccessButtonLoading(true);
			setAnimateTextDirection(true);

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'requestAccessButton',
					source: 'restrictedPageScreen',
					attributes: {
						accessType: accessType || 'unspecified',
						contentId,
						contentType,
						editDeniedButCanView,
					},
				},
			}).fire();

			await requestAccess();
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (e) {
			// handle the error
		}

		setRequestAccessButtonLoading(false);
	};

	const [animateTextDirection, setAnimateTextDirection] = useState(false);
	const [requestAccessButtonLoading, setRequestAccessButtonLoading] = useState(false);
	const restrictionsImageLightMode = accessType === 'edit' ? CantEditLightMode : CantViewLightMode;
	const restrictionsImageDarkMode = accessType === 'edit' ? CantEditDarkMode : CantViewDarkMode;

	const renderRequestSentConfirmation = () => (
		<FadeIn
			entranceDirection={animateTextDirection ? 'right' : undefined}
			distance="proportional"
			key="confirmation-text-animation"
		>
			{({ ...props }) => (
				<RequestConfirmationTextContainer {...props}>
					<Inline alignBlock="center" space="space.050" grow="hug">
						<ClockIcon label="" color={token('color.icon.subtlest')} size="small" />
						<Text color="color.text.subtlest" weight="medium">
							{accessType === 'edit' && !pendingRequestExists ? (
								<FormattedMessage
									id="no-permission.restricted.page.request.edit-access.request-sent-confirmation"
									defaultMessage="Edit requested"
									description="Confirmation message shown after edit request has been sent"
								/>
							) : (
								// This serves double purpose for confirming VIEW requests or
								// Confirming there's an existing request of unknown accessType
								// This happens when the page loads and we call the API to know if there's a request.
								// Currently it doesn't tell us what request type it is, so we make it sound generic
								// Until the API gets updated, which should be easy
								<FormattedMessage
									id="no-permission.restricted.page.request.view-access.request-sent-confirmation"
									defaultMessage="Access requested"
									description="Confirmation message shown after view request has been sent"
								/>
							)}
						</Text>
					</Inline>
				</RequestConfirmationTextContainer>
			)}
		</FadeIn>
	);

	const renderRequestAccessButton = () => (
		<FadeIn duration="large" key="primary-button-animation">
			{({ ...props }) => (
				<div {...props}>
					<Button
						onClick={handleRequestAccessClick}
						appearance="primary"
						isLoading={requestAccessButtonLoading}
					>
						{accessType === 'edit' ? (
							<FormattedMessage
								id="no-permission.restricted.page.request.edit-access"
								description="Button label for requesting edit access to a page"
								defaultMessage="Ask to edit"
							/>
						) : (
							<FormattedMessage
								id="no-permission.restricted.page.request.view-access"
								description="Button label for requesting view access to a page"
								defaultMessage="Ask for access"
							/>
						)}
					</Button>
				</div>
			)}
		</FadeIn>
	);

	const renderButtonOrExistingRequestText = () => {
		if (isLoadingForFirstTime || pendingAccessRequestExistsError || renderServerPlaceholder) {
			return <Box xcss={buttonPlaceholderStyles} />;
		}

		if (pendingRequestExists || requestSent) {
			return renderRequestSentConfirmation();
		}

		return renderRequestAccessButton();
	};

	/** Renders the access request call to action and message */
	const renderAccessRequest = () =>
		contentId ? (
			<>
				{accessType === 'edit' ? (
					<FormattedMessage
						id="no-permission.restricted.page.edit.request.access.from.author"
						description="Link to request editing access to a restricted page"
						defaultMessage="Only some people can edit this content. If you want to edit it, you can ask the owner."
					/>
				) : (
					<FormattedMessage
						id="no-permission.restricted.page.view.request.access.from.author"
						description="Link to request viewing access to a restricted page"
						defaultMessage="Only some people can view this content. If you want access, you can ask the owner."
					/>
				)}
				<ButtonContainer isMobile={shouldShowMobileWeb()}>
					{editDeniedButCanView && (
						<LinkButton href={viewLink!} onClick={makeLinkClickedHandler('main')}>
							<FormattedMessage
								id="no-permission.restricted.page.view-content"
								description="Link button label for viewing a page/blog/whiteboard/etc. the user can't edit"
								defaultMessage="View content"
							/>
						</LinkButton>
					)}
					{renderButtonOrExistingRequestText()}
				</ButtonContainer>
			</>
		) : (
			<FormattedMessage
				id="no-permission.restricted.page.manual.request.access.from.author"
				description="Instructions to manually request access to a restricted page from the page's author"
				defaultMessage="Only some people can view this. If you think you should have access, try asking the person who sent you here for help."
			/>
		);

	return (
		<>
			<ErrorPageComponent
				errorImage={restrictionsImageLightMode}
				errorImageDark={restrictionsImageDarkMode}
				errorTitle={renderTitle()}
				errorMessage={renderMessage()}
			>
				{showSuperAdminGateway && <SuperAdminGateway type="page" contentId={contentId} />}
			</ErrorPageComponent>
			<ExperienceSuccess name={RESTRICTED_PAGE_EXPERIENCE} />
			<PageLoadEnd
				metric={INACCESSIBLE_PAGE_LOAD}
				customData={{ ...CUSTOM_ATTRIBUTES, contentId }}
			/>
		</>
	);
};

const RestrictedPageWithCanViewQuery = (props: RestrictedPageProps & WithFlagsProps) => {
	const { accessType, contentId } = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const skipViewQuery = accessType !== 'edit';
	const spaceKey = useSpaceKey();
	const { replace } = useRouteActions();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const { data, error, loading } = useQueryInterop<
		RestrictedPageWithCanViewQuery$data,
		RestrictedPageWithCanViewQuery$variables
	>(RestrictedPageWithCanViewQueryGraphQL, {
		variables: { id: contentId ?? '' },
		skip: skipViewQuery,
		fetchPolicy: 'network-only',
	});

	const editDeniedButCanView = skipViewQuery
		? false
		: Boolean(data?.singleContent) && data?.singleContent?.status !== 'draft';
	const viewLink = editDeniedButCanView
		? `${CONTEXT_PATH}${data?.singleContent?.links?.webui}`
		: null;
	const canBeSuperAdmin = Boolean(data?.user?.confluence?.roles?.canBeSuperAdmin);
	// Only redirect user if:
	// 1. They have a viewLink available.
	// 2. They can't be super admin.
	const shouldRedirectToView = spaceKey && viewLink && !canBeSuperAdmin;

	useEffect(() => {
		if (shouldRedirectToView) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'redirected',
					actionSubject: 'redirectToViewMode',
					source: 'restrictedPageScreen',
					attributes: {
						accessType,
						contentId,
					},
				},
			}).fire();

			experienceTracker.succeed({
				name: RESTRICTED_PAGE_EXPERIENCE,
			});

			if (expValEquals('confluence_edit_page_restricted_redirect_ttvc_fix', 'isEnabled', true)) {
				// Abort the edit-page UFO interaction and start a new view-page transition.
				traceUFOTransition(VIEW_PAGE_LOAD_KEY, VIEW_PAGE.name);
				// View page route is not SSR-rendered because this is a transition
				window['__SSR_RENDERED__'] = false;
			}

			replace(
				VIEW_PAGE.toUrl(
					{
						spaceKey,
						contentId,
					},
					{
						query: {
							redirectedFromRestrict: 'true',
						},
					},
				),
			);
		}
	}, [
		createAnalyticsEvent,
		contentId,
		shouldRedirectToView,
		accessType,
		spaceKey,
		replace,
		experienceTracker,
	]);

	if (loading) {
		return <ShowSpinnerAfterDelay />;
	} else {
		/**
		 * Usually, if the user doesn't have permission to view the page, the
		 * query succeeds with `singleContent: null`. But occasionally we get a
		 * 401 or 403, and we'd consider those to be expected errors.
		 */
		let hasUnhandledError = false;
		if (error) {
			if (is401(error)) markErrorAsHandled(error);
			else if (is403(error)) markErrorAsHandled(error);
			else hasUnhandledError = true;
		}

		return shouldRedirectToView ? (
			<ShowSpinnerAfterDelay />
		) : (
			<>
				{hasUnhandledError && (
					<>
						<ExperienceFailure name={RESTRICTED_PAGE_EXPERIENCE} error={error!} />
						<ErrorDisplay error={error!} />
					</>
				)}
				<RestrictedPageComponent
					{...props}
					editDeniedButCanView={editDeniedButCanView}
					viewLink={viewLink}
				/>
			</>
		);
	}
};

export const RestrictedPage = (props: RestrictedPageProps) => (
	<ErrorBoundary attribution={Attribution.ADMIN_EXPERIENCE}>
		<ExperienceStart name={RESTRICTED_PAGE_EXPERIENCE} />
		<Subscribe to={[FlagsStateContainer]}>
			{(flags: FlagsStateContainer) => <RestrictedPageWithCanViewQuery {...props} flags={flags} />}
		</Subscribe>
	</ErrorBoundary>
);
